import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SiteOverview from './pages/SiteOverview';
import SiteKeywords from './pages/SiteKeywords';
import SiteScanner from './pages/SiteScanner';
import SiteChecklist from './pages/SiteChecklist';
import SiteSettings from './pages/SiteSettings';
import AddSite from './pages/AddSite';
import Costs from './pages/Costs';
import SystemAdmin from './pages/SystemAdmin';
import SiteContent from './pages/SiteContent';
import SiteSuccessStories from './pages/SiteSuccessStories';
import SiteKeywordResearch from './pages/SiteKeywordResearch';
import SiteReports from './pages/SiteReports';
import ReportsOverview from './pages/ReportsOverview';
import Uptime from './pages/Uptime';
import PaidAds from './pages/PaidAds';
import MonthlyPlan from './pages/MonthlyPlan';
import SiteCompetitors from './pages/SiteCompetitors';
import SiteLinks from './pages/SiteLinks';
import SiteGSC from "./pages/SiteGSC";
import SiteAnalytics from "./pages/SiteAnalytics";
import GeoDashboard from './pages/GeoDashboard';
import GeoAuditPage from './pages/GeoAuditPage';
import GeoCitations from './pages/GeoCitations';
import GeoMeasurement from './pages/GeoMeasurement';
import GeoOverview from './pages/GeoOverview';
import './styles.css';
import { ThemeProvider } from './ThemeContext';

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('maxrank_user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('maxrank_token') || null);
  const [authChecked, setAuthChecked] = useState(false);

  // Verify token on load
  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    fetch((process.env.PUBLIC_URL || '') + '/api/user-auth/me', {
      headers: { 'Authorization': 'Bearer ' + token },
    })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(u => {
        setUser(u);
        localStorage.setItem('maxrank_user', JSON.stringify(u));
        setAuthChecked(true);
      })
      .catch(() => {
        // Token expired/invalid
        setUser(null);
        setToken(null);
        localStorage.removeItem('maxrank_token');
        localStorage.removeItem('maxrank_user');
        setAuthChecked(true);
      });
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('maxrank_token', authToken);
    localStorage.setItem('maxrank_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('maxrank_token');
    localStorage.removeItem('maxrank_user');
  };

  // Show nothing while verifying token
  if (!authChecked) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ user, token, logout: handleLogout }}>
        <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
          {!user ? (
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          ) : (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/add-site" element={<AddSite />} />
                <Route path="/costs" element={<Costs />} />
                <Route path="/reports" element={<ReportsOverview />} />
                <Route path="/uptime" element={<Uptime />} />
                <Route path="/system" element={<SystemAdmin />} />
                <Route path="/sites/:id" element={<SiteOverview />} />
                <Route path="/sites/:id/keywords" element={<SiteKeywords />} />
                <Route path="/sites/:id/gsc" element={<SiteGSC />} />
                <Route path="/sites/:id/analytics" element={<SiteAnalytics />} />
                <Route path="/sites/:siteId/competitors" element={<SiteCompetitors />} />
                <Route path="/sites/:id/content" element={<SiteContent />} />
                <Route path="/sites/:id/success-stories" element={<SiteSuccessStories />} />
                <Route path="/sites/:id/keyword-research" element={<SiteKeywordResearch />} />
                <Route path="/sites/:id/checklist" element={<SiteChecklist />} />
                <Route path="/sites/:id/health" element={<SiteScanner />} />
                <Route path="/sites/:id/links" element={<SiteLinks />} />
                <Route path="/sites/:id/geo" element={<GeoDashboard />} />
                <Route path="/sites/:id/geo-measurement" element={<GeoMeasurement />} />
                <Route path="/geo-measurement" element={<GeoOverview />} />
                <Route path="/geo" element={<GeoDashboard />} />
                <Route path="/geo/audit/:auditId" element={<GeoAuditPage />} />
                <Route path="/geo/citations/:siteId" element={<GeoCitations />} />
                <Route path="/sites/:id/reports" element={<SiteReports />} />
                <Route path="/sites/:id/paid-ads" element={<PaidAds />} />
                <Route path="/sites/:id/workplan" element={<MonthlyPlan />} />
                <Route path="/sites/:id/settings" element={<SiteSettings />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          )}
        </BrowserRouter>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;